import React, { useEffect, useState } from 'react';
import { HStack, VStack, Text, Divider, Skeleton } from '@chakra-ui/react';
import { supabase } from '../../supabaseClient';
import { selectAll } from '../../utils/fetchAllRows';
import { effectiveDrawPrice } from '../../utils/invoicePricing';

// Compact invoice summary shown next to the "Billing & Transactions" heading.
// Replaces the large Invoice Overview card. Amounts use the per-draw price
// model (effectiveDrawPrice), so they match the invoice list.
const money = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n || 0);

function Stat({ label, value, sub, color = 'gray.800' }) {
  return (
    <VStack spacing={0} align="center" minW="64px">
      <Text fontSize="lg" fontWeight="bold" color={color} lineHeight="1.1">{value}</Text>
      <Text fontSize="2xs" color="gray.500" textTransform="uppercase" letterSpacing="wide">{label}</Text>
      {sub != null && <Text fontSize="2xs" color="gray.600" fontWeight="semibold">{sub}</Text>}
    </VStack>
  );
}

export default function InvoiceStatsStrip() {
  const [loading, setLoading] = useState(true);
  const [s, setS] = useState({ total: 0, unpaid: 0, unpaidAmt: 0, paid: 0, paidAmt: 0, rate: 0 });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [{ data: invoices }, { data: batches }] = await Promise.all([
          supabase.from('submission_invoices').select('id, payment_status, original_batch_id, ship_fee'),
          supabase.from('submission_batches').select('id, ship_fee'),
        ]);
        const invs = invoices || [];
        const batchIds = [...new Set(invs.map((i) => i.original_batch_id).filter(Boolean))];
        let subsByBatch = {};
        if (batchIds.length) {
          const { data: subs } = await selectAll(() => supabase
            .from('submissions')
            .select('id, batch_id, invoice_price')
            .in('batch_id', batchIds));
          (subs || []).forEach((sub) => {
            (subsByBatch[sub.batch_id] = subsByBatch[sub.batch_id] || []).push(sub);
          });
        }
        const batchShip = {};
        (batches || []).forEach((b) => { batchShip[b.id] = parseFloat(b.ship_fee || 0); });
        const amountOf = (inv) => {
          const subs = subsByBatch[inv.original_batch_id] || [];
          const draws = subs.reduce((sum, x) => sum + effectiveDrawPrice(x), 0);
          return draws + (batchShip[inv.original_batch_id] || 0);
        };
        let total = invs.length, unpaid = 0, unpaidAmt = 0, paid = 0, paidAmt = 0;
        invs.forEach((inv) => {
          const amt = amountOf(inv);
          if (inv.payment_status === 'paid') { paid++; paidAmt += amt; }
          else { unpaid++; unpaidAmt += amt; }
        });
        if (!cancelled) {
          setS({ total, unpaid, unpaidAmt, paid, paidAmt, rate: total ? Math.round((paid / total) * 100) : 0 });
          setLoading(false);
        }
      } catch {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) return <Skeleton height="34px" width="320px" borderRadius="md" />;

  return (
    <HStack spacing={4} divider={<Divider orientation="vertical" h="28px" />} align="center">
      <Stat label="Invoices" value={s.total} />
      <Stat label="Unpaid" value={s.unpaid} sub={money(s.unpaidAmt)} color="yellow.600" />
      <Stat label="Paid" value={s.paid} sub={money(s.paidAmt)} color="green.600" />
      <Stat label="Paid rate" value={`${s.rate}%`} color="blue.600" />
    </HStack>
  );
}
